import { ErrorState } from '@/components/primitives/ErrorState'
import { PageHeader } from '@/components/primitives/PageHeader'
import { Switch } from '@/components/ui/switch'
import { fetchModels } from '@/lib/ai-team'
import {
  type ScheduleKind,
  type BlogPostStatus,
  type BlogPostType,
  type BloggerPost,
  type BloggerRun,
  type BloggerState,
  type BloggerUpdate,
  blogPostAction,
  fetchBlogger,
  runBloggerNow,
  updateBlogger
} from '@/lib/blogger'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  ExternalLink,
  Loader2,
  Megaphone,
  Newspaper,
  PenLine
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://xoblack.com'

const card =
  'rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900'
const field =
  'w-full rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white'
const primary =
  'inline-flex items-center justify-center gap-2 rounded-[36px] bg-neutral-900 px-6 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-neutral-200'
const ghost =
  'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-300 dark:hover:bg-neutral-800'

const TYPE_LABELS: Record<BlogPostType, string> = {
  feature: 'Features',
  insight: 'Insights',
  community: 'Community'
}

const STATUS_STYLES: Record<BlogPostStatus, { label: string; className: string }> = {
  published: {
    label: 'Live',
    className:
      'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
  },
  pending_review: {
    label: 'Needs review',
    className:
      'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400'
  },
  draft: {
    label: 'Draft',
    className:
      'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
  },
  archived: {
    label: 'Archived',
    className:
      'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
  }
}

const INTERVALS = [
  { seconds: 21600, label: 'Every 6 hours' },
  { seconds: 43200, label: 'Twice a day' },
  { seconds: 172800, label: 'Every 2 days' },
  { seconds: 302400, label: 'Twice a week' }
]

const SCHEDULE_KINDS: { key: ScheduleKind; label: string }[] = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'interval', label: 'Custom interval' }
]

// 0 = Monday, matching Python's weekday().
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern (New York)' },
  { id: 'America/Chicago', label: 'Central (Chicago)' },
  { id: 'America/Denver', label: 'Mountain (Denver)' },
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { id: 'UTC', label: 'UTC' }
]

function ordinal(n: number) {
  const suffix = n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'
  return `${n}${suffix}`
}

function clock(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number)
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function when(iso: string | null, timeZone?: string) {
  if (!iso) return 'Never'
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone
  })
}

export function BloggerPage() {
  const [queuedAt, setQueuedAt] = useState<number | null>(null)
  const query = useQuery({
    queryKey: ['blogger'],
    queryFn: fetchBlogger,
    // Poll briefly after "Write now" so the new run and post show up.
    refetchInterval: queuedAt && Date.now() - queuedAt < 5 * 60_000 ? 10_000 : 60_000
  })
  const models = useQuery({ queryKey: ['ai-models'], queryFn: fetchModels })
  const [saving, setSaving] = useState<string | null>(null)

  async function save(key: string, body: BloggerUpdate, done: string) {
    setSaving(key)
    try {
      queryClient.setQueryData(['blogger'], await updateBlogger(body))
      toast.success(done)
    } catch (err) {
      notifyError('Could not save', err)
    } finally {
      setSaving(null)
    }
  }

  const data = query.data

  return (
    <div className="mx-auto w-full max-w-6xl">
      <PageHeader
        title="Blog writer"
        description="Writes one SEO post for xoblack.com/blog on a schedule: Features, Insights and Community. It never promotes businesses — City Guides does that."
        actions={
          data && (
            <label className="flex items-center gap-3 rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white">
              {saving === 'killed' && (
                <Loader2 className="size-4 animate-spin text-neutral-400" />
              )}
              {data.agent.is_killed ? 'Off' : 'On'}
              <Switch
                checked={!data.agent.is_killed}
                disabled={!!saving}
                aria-label="Blog writer on or off"
                onCheckedChange={(on) =>
                  save(
                    'killed',
                    { is_killed: !on },
                    on ? 'The Blog writer is on.' : 'The Blog writer is off.'
                  )
                }
              />
            </label>
          )
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : !data ? (
        <Skeleton />
      ) : (
        <div className="space-y-8">
          <Stats data={data} />

          <div className="grid gap-4 lg:grid-cols-2">
            <WriteNow
              data={data}
              onQueued={() => {
                setQueuedAt(Date.now())
                query.refetch()
              }}
            />
            <ScheduleCard data={data} saving={saving} save={save} />
          </div>

          <div>
            <SettingsCard
              data={data}
              models={models.data?.text ?? []}
              saving={saving}
              save={save}
            />
          </div>

          <PostsSection data={data} />
          <RunsSection runs={data.runs} />
        </div>
      )}
    </div>
  )
}

function Stats({ data }: { data: BloggerState }) {
  const cap = data.settings.posts_per_day
  const stats = [
    { label: 'Live on the blog', value: data.stats.published },
    { label: 'Needs review', value: data.stats.pending_review },
    {
      label: 'Written today',
      value: cap ? `${data.stats.written_today} / ${cap}` : data.stats.written_today
    },
    {
      label: 'Next run',
      value: data.agent.is_killed
        ? 'Off'
        : when(data.agent.next_run_at, data.agent.schedule_timezone)
    }
  ]
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-[36px] border border-neutral-200 bg-neutral-50 px-4 py-4 text-center dark:border-neutral-800 dark:bg-neutral-900/40"
        >
          <p className="line-clamp-2 text-lg font-bold tabular-nums text-neutral-900 dark:text-white">
            {s.value}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {s.label}
          </p>
        </div>
      ))}
    </div>
  )
}

function WriteNow({
  data,
  onQueued
}: {
  data: BloggerState
  onQueued: () => void
}) {
  const [postType, setPostType] = useState<BlogPostType | ''>('')
  const [topic, setTopic] = useState('')
  const [pending, setPending] = useState(false)
  const capReached =
    data.settings.posts_per_day > 0 &&
    data.stats.written_today >= data.settings.posts_per_day
  const blocked = data.agent.is_killed || capReached

  async function write() {
    setPending(true)
    try {
      await runBloggerNow({
        post_type: postType || undefined,
        topic: topic.trim() || undefined
      })
      toast.success('Writing now — the post shows up below in a minute or two.')
      setTopic('')
      onQueued()
    } catch (err) {
      notifyError('Could not start the Blog writer', err)
    } finally {
      setPending(false)
    }
  }

  const description = data.post_types.find((t) => t.key === postType)?.description

  return (
    <section className={cn(card, 'space-y-4')}>
      <div>
        <h2 className="font-bold text-neutral-900 dark:text-white">
          Write a post now
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Skips the schedule. Leave everything blank and it picks the type and
          topic itself.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Post type">
        {[{ key: '' as const, label: 'Let it choose' }, ...data.post_types.map((t) => ({ key: t.key, label: TYPE_LABELS[t.key] }))].map(
          (t) => (
            <button
              key={t.key || 'any'}
              type="button"
              role="radio"
              aria-checked={postType === t.key}
              onClick={() => setPostType(t.key)}
              className={cn(
                'rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors',
                postType === t.key
                  ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
            >
              {t.label}
            </button>
          )
        )}
      </div>
      {description && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {description}
        </p>
      )}

      <div className="space-y-1.5">
        <label
          htmlFor="blogger-topic"
          className="text-xs font-semibold text-neutral-700 dark:text-neutral-300"
        >
          Topic (optional)
        </label>
        <input
          id="blogger-topic"
          value={topic}
          maxLength={300}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g. how to find Juneteenth events near me"
          className={field}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={write}
          disabled={pending || blocked}
          className={primary}
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <PenLine className="size-4" />
          )}
          Write now
        </button>
        {data.agent.is_killed ? (
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
            Turn the Blog writer on first.
          </span>
        ) : capReached ? (
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
            Today's limit is reached. Raise "Posts per day" to write more.
          </span>
        ) : null}
      </div>
    </section>
  )
}

function NumberSetting({
  id,
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onSave
}: {
  id: string
  label: string
  hint: string
  value: number
  min: number
  max: number
  disabled: boolean
  onSave: (value: number) => void
}) {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => setDraft(String(value)), [value])

  function commit() {
    const next = Number(draft)
    if (draft === '' || Number.isNaN(next) || next < min || next > max) {
      toast.error(`${label}: use a number from ${min} to ${max}.`)
      setDraft(String(value))
      return
    }
    if (next !== value) onSave(next)
  }

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-xs font-semibold text-neutral-700 dark:text-neutral-300"
      >
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        className={field}
      />
      <p className="text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>
    </div>
  )
}

function ScheduleCard({
  data,
  saving,
  save
}: {
  data: BloggerState
  saving: string | null
  save: (key: string, body: BloggerUpdate, done: string) => void
}) {
  const a = data.agent
  const [time, setTime] = useState(a.schedule_time)
  useEffect(() => setTime(a.schedule_time), [a.schedule_time])
  const tzLabel =
    TIMEZONES.find((t) => t.id === a.schedule_timezone)?.label.split(' ')[0] ??
    a.schedule_timezone
  const timezones = TIMEZONES.some((t) => t.id === a.schedule_timezone)
    ? TIMEZONES
    : [{ id: a.schedule_timezone, label: a.schedule_timezone }, ...TIMEZONES]
  const intervals = INTERVALS.some((i) => i.seconds === a.polling_interval_seconds)
    ? INTERVALS
    : [
        {
          seconds: a.polling_interval_seconds,
          label: `Every ${Math.round(a.polling_interval_seconds / 3600)} hours`
        },
        ...INTERVALS
      ]

  const summary =
    a.schedule_kind === 'daily'
      ? `Every day at ${clock(a.schedule_time)} ${tzLabel}`
      : a.schedule_kind === 'weekly'
        ? `Every ${WEEKDAY_NAMES[a.schedule_weekday]} at ${clock(a.schedule_time)} ${tzLabel}`
        : a.schedule_kind === 'monthly'
          ? `The ${ordinal(a.schedule_day_of_month)} of every month at ${clock(a.schedule_time)} ${tzLabel}`
          : (intervals.find((i) => i.seconds === a.polling_interval_seconds)?.label ?? 'Custom interval')

  const pill = (active: boolean) =>
    cn(
      'rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50',
      active
        ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
        : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800'
    )
  const label = 'text-xs font-semibold text-neutral-700 dark:text-neutral-300'

  return (
    <section className={cn(card, 'space-y-4')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-bold text-neutral-900 dark:text-white">Schedule</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {a.is_killed ? 'Paused — turn the Blog writer on to resume.' : summary}
          </p>
        </div>
        {saving?.startsWith('schedule') && (
          <Loader2 className="size-4 shrink-0 animate-spin text-neutral-400" />
        )}
      </div>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How often">
        {SCHEDULE_KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            role="radio"
            aria-checked={a.schedule_kind === k.key}
            disabled={!!saving}
            onClick={() =>
              a.schedule_kind !== k.key &&
              save('schedule-kind', { schedule_kind: k.key }, `Schedule set to ${k.label.toLowerCase()}.`)
            }
            className={pill(a.schedule_kind === k.key)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {a.schedule_kind === 'weekly' && (
        <div className="space-y-1.5">
          <p className={label}>Day of the week</p>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Day of the week">
            {WEEKDAYS.map((day, i) => (
              <button
                key={day}
                type="button"
                role="radio"
                aria-checked={a.schedule_weekday === i}
                aria-label={WEEKDAY_NAMES[i]}
                disabled={!!saving}
                onClick={() =>
                  save('schedule-day', { schedule_weekday: i }, `Runs every ${WEEKDAY_NAMES[i]}.`)
                }
                className={cn(pill(a.schedule_weekday === i), 'px-3')}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {a.schedule_kind === 'monthly' && (
          <div className="space-y-1.5">
            <label htmlFor="blogger-dom" className={label}>
              Day of the month
            </label>
            <select
              id="blogger-dom"
              value={a.schedule_day_of_month}
              disabled={!!saving}
              onChange={(e) =>
                save(
                  'schedule-dom',
                  { schedule_day_of_month: Number(e.target.value) },
                  `Runs on the ${ordinal(Number(e.target.value))} of each month.`
                )
              }
              className={field}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {ordinal(d)}
                </option>
              ))}
            </select>
          </div>
        )}

        {a.schedule_kind === 'interval' ? (
          <div className="space-y-1.5 sm:col-span-2">
            <label htmlFor="blogger-interval" className={label}>
              Run every
            </label>
            <select
              id="blogger-interval"
              value={a.polling_interval_seconds}
              disabled={!!saving}
              onChange={(e) =>
                save('schedule-interval', { polling_interval_seconds: Number(e.target.value) }, 'Interval saved.')
              }
              className={field}
            >
              {intervals.map((i) => (
                <option key={i.seconds} value={i.seconds}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <label htmlFor="blogger-time" className={label}>
                Time
              </label>
              <input
                id="blogger-time"
                type="time"
                step={1800}
                value={time}
                disabled={!!saving}
                onChange={(e) => setTime(e.target.value)}
                onBlur={() =>
                  time && time !== a.schedule_time &&
                  save('schedule-time', { schedule_time: time }, `Runs at ${clock(time)}.`)
                }
                className={field}
              />
            </div>
            <div className={cn('space-y-1.5', a.schedule_kind === 'monthly' && 'sm:col-span-2')}>
              <label htmlFor="blogger-tz" className={label}>
                Timezone
              </label>
              <select
                id="blogger-tz"
                value={a.schedule_timezone}
                disabled={!!saving}
                onChange={(e) =>
                  save('schedule-tz', { schedule_timezone: e.target.value }, 'Timezone saved.')
                }
                className={field}
              >
                {timezones.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-[36px] bg-neutral-50 px-4 py-3 text-xs dark:bg-neutral-950">
        <span className="text-neutral-500 dark:text-neutral-400">
          Next run{' '}
          <span className="font-semibold text-neutral-900 dark:text-white">
            {a.is_killed ? 'Paused' : when(a.next_run_at, a.schedule_timezone)}
          </span>
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">
          Last run{' '}
          <span className="font-semibold text-neutral-900 dark:text-white">
            {when(a.last_run_at, a.schedule_timezone)}
          </span>
        </span>
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Runs within about 30 minutes of the set time. "Posts per day" still caps
        how many it writes.
      </p>
    </section>
  )
}

function SettingsCard({
  data,
  models,
  saving,
  save
}: {
  data: BloggerState
  models: { id: string; name: string }[]
  saving: string | null
  save: (key: string, body: BloggerUpdate, done: string) => void
}) {
  const modelOptions = models.some((m) => m.id === data.agent.llm_model)
    ? models
    : [{ id: data.agent.llm_model, name: data.agent.llm_model }, ...models]
  return (
    <section className={cn(card, 'space-y-4')}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-neutral-900 dark:text-white">Settings</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Saved as soon as you change them.
          </p>
        </div>
        {saving && saving !== 'killed' && (
          <Loader2 className="size-4 animate-spin text-neutral-400" />
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2 xl:col-span-1">
          <label
            htmlFor="blogger-model"
            className="text-xs font-semibold text-neutral-700 dark:text-neutral-300"
          >
            Writing model
          </label>
          <select
            id="blogger-model"
            value={data.agent.llm_model}
            disabled={!!saving}
            onChange={(e) =>
              save('model', { llm_model: e.target.value }, 'Model saved.')
            }
            className={field}
          >
            {modelOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Every post is drafted, then rewritten by an editor pass. Small
            models write thin posts that get held for review; GPT-4.1 is
            recommended.
          </p>
        </div>

        <NumberSetting
          id="blogger-per-day"
          label="Posts per day"
          hint="Hard limit, including Write now. 0 = no limit."
          value={data.settings.posts_per_day}
          min={0}
          max={24}
          disabled={!!saving}
          onSave={(v) =>
            save('per-day', { posts_per_day: v }, 'Daily limit saved.')
          }
        />

        <NumberSetting
          id="blogger-score"
          label="Auto-publish score"
          hint="Posts scoring this or more (and 700+ words) go live; the rest wait for review."
          value={data.settings.auto_publish_min_score}
          min={0}
          max={100}
          disabled={!!saving}
          onSave={(v) =>
            save('score', { auto_publish_min_score: v }, 'Publish score saved.')
          }
        />

        <div className="flex items-center justify-between gap-3 rounded-[36px] border border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900 dark:text-white">
              Show /blog on the site
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Off hides the whole blog.
            </p>
          </div>
          <Switch
            checked={data.settings.enable_blog}
            disabled={!!saving}
            aria-label="Show the blog on the site"
            onCheckedChange={(on) =>
              save(
                'enable',
                { enable_blog: on },
                on ? 'The blog is visible.' : 'The blog is hidden.'
              )
            }
          />
        </div>

        <div className="flex items-center justify-between gap-3 rounded-[36px] border border-neutral-200 px-4 py-3 sm:col-span-2 xl:col-span-2 dark:border-neutral-800">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900 dark:text-white">
              Share new posts to the feed
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Each post that goes live is posted to the feed with its cover and
              link. Unpublishing hides it again.
            </p>
          </div>
          <Switch
            checked={data.settings.share_to_feed}
            disabled={!!saving}
            aria-label="Share new blog posts to the feed"
            onCheckedChange={(on) =>
              save(
                'feed',
                { share_to_feed: on },
                on
                  ? 'New posts will be shared to the feed.'
                  : 'New posts stay on the blog only.'
              )
            }
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2 xl:col-span-2">
          <label
            htmlFor="blogger-feed-account"
            className="text-xs font-semibold text-neutral-700 dark:text-neutral-300"
          >
            Post to the feed as
          </label>
          <select
            id="blogger-feed-account"
            value={
              data.settings.feed_account_is_default
                ? ''
                : data.settings.feed_account.id
            }
            disabled={!!saving}
            onChange={(e) =>
              save(
                'feed-account',
                {
                  feed_account_id: e.target.value
                    ? Number(e.target.value)
                    : null
                },
                'Feed account saved.'
              )
            }
            className={field}
          >
            <option value="">XOBLACK Editorial (default)</option>
            {data.staff_accounts.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name === u.username ? `@${u.username}` : `${u.name} (@${u.username})`}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Any staff account. Staff can also share a post from their own
            account with the "Share to feed" button on the blog page.
          </p>
        </div>
      </div>
    </section>
  )
}

function PostsSection({ data }: { data: BloggerState }) {
  const [busy, setBusy] = useState<string | null>(null)

  async function act(
    post: BloggerPost,
    action: 'publish' | 'unpublish' | 'archive' | 'share'
  ) {
    setBusy(`${post.id}:${action}`)
    try {
      queryClient.setQueryData(['blogger'], await blogPostAction(post.id, action))
      toast.success(
        action === 'publish'
          ? 'Published.'
          : action === 'unpublish'
            ? 'Taken off the blog.'
            : action === 'share'
              ? `Posted to the feed as ${data.settings.feed_account.name}.`
              : 'Archived.'
      )
    } catch (err) {
      notifyError('Could not update the post', err)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="font-bold text-neutral-900 dark:text-white">Posts</h2>
      {data.posts.length === 0 ? (
        <div className="rounded-[36px] border border-neutral-200 bg-neutral-50/60 px-5 py-10 text-center dark:border-neutral-800 dark:bg-neutral-900/40">
          <Newspaper className="mx-auto size-8 text-neutral-400" />
          <p className="mt-3 text-sm font-semibold text-neutral-900 dark:text-white">
            No posts yet
          </p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Use Write now above, or wait for the next scheduled run.
          </p>
        </div>
      ) : (
        <div className={cn(card, 'divide-y divide-neutral-200 p-0 dark:divide-neutral-800')}>
          {data.posts.map((post) => {
            const status = STATUS_STYLES[post.status]
            return (
              <div
                key={post.id}
                className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <p className="truncate font-semibold text-neutral-900 dark:text-white">
                    {post.title}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                    <span className={cn('rounded-full px-2.5 py-0.5 font-semibold', status.className)}>
                      {status.label}
                    </span>
                    <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 font-semibold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                      {TYPE_LABELS[post.post_type] ?? post.post_type}
                    </span>
                    {post.in_feed && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 font-semibold text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                        <Megaphone className="size-3" />
                        In feed
                      </span>
                    )}
                    <span>
                      Score {post.seo_score ?? '—'} · {post.word_count.toLocaleString()} words
                    </span>
                    <span className="truncate">“{post.focus_keyword}”</span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1">
                  {post.status === 'published' ? (
                    <>
                      <a
                        href={`${SITE_URL}/blog/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={ghost}
                      >
                        <ExternalLink className="size-3.5" /> View
                      </a>
                      {!post.in_feed && (
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() => act(post, 'share')}
                          className={ghost}
                        >
                          {busy === `${post.id}:share` ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Megaphone className="size-3.5" />
                          )}
                          Share to feed
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => act(post, 'unpublish')}
                        className={ghost}
                      >
                        {busy === `${post.id}:unpublish` && <Loader2 className="size-3.5 animate-spin" />}
                        Unpublish
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => act(post, 'publish')}
                      className={ghost}
                    >
                      {busy === `${post.id}:publish` && <Loader2 className="size-3.5 animate-spin" />}
                      Publish
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => act(post, 'archive')}
                    className={cn(ghost, 'text-red-500 dark:text-red-400')}
                  >
                    {busy === `${post.id}:archive` && <Loader2 className="size-3.5 animate-spin" />}
                    Archive
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

const RUN_STYLES: Record<BloggerRun['status'], string> = {
  success: 'text-emerald-600 dark:text-emerald-400',
  running: 'text-amber-600 dark:text-amber-400',
  pending: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-500 dark:text-red-400',
  killed: 'text-red-500 dark:text-red-400',
  skipped: 'text-neutral-500 dark:text-neutral-400'
}

function RunsSection({ runs }: { runs: BloggerRun[] }) {
  return (
    <section className="space-y-3">
      <h2 className="font-bold text-neutral-900 dark:text-white">Recent runs</h2>
      {runs.length === 0 ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          It hasn't run yet.
        </p>
      ) : (
        <div className={cn(card, 'divide-y divide-neutral-200 p-0 dark:divide-neutral-800')}>
          {runs.map((run) => (
            <div key={run.id} className="space-y-1 px-5 py-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={cn('font-bold capitalize', RUN_STYLES[run.status])}>
                  {run.status}
                </span>
                <span className="text-neutral-500 dark:text-neutral-400">
                  {when(run.created_at)} · {run.trigger_source === 'manual' ? 'Write now' : 'Scheduled'}
                </span>
              </div>
              {run.summary && (
                <p className="line-clamp-2 text-xs text-neutral-600 dark:text-neutral-300">
                  {run.summary}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function Skeleton() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-20 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
          />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-72 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
          />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
    </div>
  )
}
